import { z } from 'zod'

/** The languages an event description can be written or translated in. */
export const eventDescriptionLanguageValues = ['de', 'en'] as const
export type EventDescriptionLanguage = (typeof eventDescriptionLanguageValues)[number]
export const eventDescriptionLanguageSchema = z.enum(eventDescriptionLanguageValues)

/** The description in further languages: HTML keyed by language. */
export const eventDescriptionTranslationsSchema = z.strictObject({
  de: z.string().optional(),
  en: z.string().optional(),
})
export type EventDescriptionTranslations = z.infer<typeof eventDescriptionTranslationsSchema>

/** Credit cost of translating one event description into one further language. */
export const EVENT_TRANSLATION_CREDIT_COST = 20

export const translateDescriptionRequestSchema = z.object({
  html: z.string().trim().min(1).max(20_000),
  from: eventDescriptionLanguageSchema,
  to: eventDescriptionLanguageSchema,
  /** A flow run whose package may still cover the translation. */
  flowId: z.string().uuid().optional(),
})
export type TranslateDescriptionRequest = z.infer<typeof translateDescriptionRequestSchema>

export const translateDescriptionResponseSchema = z.object({ html: z.string() })
export type TranslateDescriptionResponse = z.infer<typeof translateDescriptionResponseSchema>

/** Picks a language from a locale tag such as `en-US`; everything else reads German. */
export function eventDescriptionLanguageOf(
  locale: string | null | undefined,
): EventDescriptionLanguage {
  return locale?.toLowerCase().startsWith('en') ? 'en' : 'de'
}

/** The event description in `lang` when the organizer has it, else the original. */
export function eventDescriptionFor(
  event: {
    description: string
    descriptionLanguage?: EventDescriptionLanguage | null
    descriptionTranslations?: EventDescriptionTranslations | null
  },
  lang: string | null | undefined,
): string {
  const wanted = eventDescriptionLanguageOf(lang)
  if (wanted === (event.descriptionLanguage ?? 'de')) return event.description
  const translated = event.descriptionTranslations?.[wanted]
  return translated?.trim() ? translated : event.description
}
